import * as React from 'react'
import styled from 'styled-components'

import { Button, Option, Loader, Select, breakpoint, color, icons, styles as S } from '@bob/design-system'
import * as hooks from '@bob/hooks'

type SortProps<ITEM> = {
  engine: hooks.useSearchEngine.SearchEngine<ITEM>
  label: string
}

export function Sort<ITEM>({
  engine: { ready, query, result, sort },
  label,
}: SortProps<ITEM>): React.ReactElement | null {
  const selectedSort = ready
    ? sort.configSort.filter((sortItem) => {
        return sortItem.type === query.value.sortType
      })
    : [{ name: 'Chargement', type: hooks.useSearchEngine.SortType.loading } as hooks.useSearchEngine.Sort<ITEM>]

  return (
    <Container>
      <MobileContainer>
        <Button iconLeft={<icons.Sliders />} outlined>
          <S.copy.TextTag>{selectedSort?.[0].name}</S.copy.TextTag>
        </Button>
        <SortSelect sort={sort} selectedSort={selectedSort} />
      </MobileContainer>
      <DesktopContainer>
        <SortWrapper>
          <S.copy.Text2>
            {result.status === 'success' ? result.total : <Loader size={14} />}
            &nbsp;{label}
          </S.copy.Text2>
          <SortSelect sort={sort} selectedSort={selectedSort} />
        </SortWrapper>
      </DesktopContainer>
    </Container>
  )
}

type SortSelectProps<ITEM> = {
  sort: hooks.useSearchEngine.SearchEngine<ITEM>['sort']
  selectedSort: hooks.useSearchEngine.Sort<ITEM>[]
}
function SortSelect<ITEM>({ sort, selectedSort }: SortSelectProps<ITEM>): React.ReactElement {
  return (
    <Select
      borderless
      onChange={(selected) => {
        sort.sort(selected[0].value)
      }}
      placeholder={selectedSort[0].name}
      status="focused"
    >
      {sort.configSort.map((item) => {
        return (
          <Option label={item.name} value={item.type} key={item.type}>
            {item.name}
          </Option>
        )
      })}
    </Select>
  )
}

const Container = styled.div`
  margin-left: 3px;

  ${S.copy.TextTag} {
    color: ${color.GRAY_1};
    display: inline-block;
  }

  @media not screen and (${breakpoint.MOBILE_M}) {
    ${S.button.Text} {
      margin: 0px;
    }

    ${S.copy.TextTag} {
      display: none;
    }
  }
`

const MobileContainer = styled.div`
  display: none;
  position: relative;

  ${Button} {
    pointer-events: none;
    position: relative;
    background-color: ${color.SAND_1};
  }

  ${Select} {
    position: absolute;
    top: 0px;
    left: 0px;
    border: 0px;
    width: 100%;

    ${S.form.select.Button} {
      opacity: 0;
    }

    ${S.form.select.ResponsiveDrawer} {
      z-index: 2;
    }
  }

  @media not screen and (${breakpoint.TABLET}) {
    display: block;
  }
`

const DesktopContainer = styled.div`
  display: block;

  ${S.copy.Text2} {
    position: relative;
    top: -2px;
  }

  @media not screen and (${breakpoint.TABLET}) {
    display: none;
  }
`

const SortWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  position: relative;
  z-index: 2;

  ${Select} {
    margin-left: 8px;
  }

  ${Loader} {
    display: inline;
    margin-right: 4px;
  }
`
